import { z } from 'zod';
import { gameViewSchema } from './game.js';

/** Games that ended with at least one guess. A game opened and replaced untouched is not history. */
export const gameSummarySchema = z.object({
  id: z.string(),
  status: z.enum(['won', 'abandoned']),
  startedAt: z.string(),
  endedAt: z.string(),
  elapsedMs: z.number().int().min(0),
  guessCount: z.number().int().min(1),
  answer: z.object({ championId: z.string() }),
  /** Guesses the solver needs for this answer: the baseline for pace. */
  par: z.number().int().min(1),
});
export type GameSummary = z.infer<typeof gameSummarySchema>;

export const gameListResponseSchema = z.object({
  games: z.array(gameSummarySchema),
  /** Pass as `before` to get the next older page; null when there is none. */
  nextBefore: z.string().nullable(),
});
export type GameListResponse = z.infer<typeof gameListResponseSchema>;

export const guessAnalysisViewSchema = z.object({
  seq: z.number().int().min(1),
  candidatesBefore: z.number().int().min(0),
  candidatesAfter: z.number().int().min(0),
  /** The guess was still a possible answer given everything known before it. */
  consistent: z.boolean(),
  /** Candidates removed divided by the most any available guess could have removed, 0 to 1. */
  efficiency: z.number().min(0).max(1),
});
export type GuessAnalysisView = z.infer<typeof guessAnalysisViewSchema>;

export const replayResponseSchema = z.object({
  game: gameViewSchema,
  par: z.number().int().min(1),
  analysis: z.array(guessAnalysisViewSchema),
});
export type ReplayResponse = z.infer<typeof replayResponseSchema>;

export const HISTOGRAM_BUCKETS = 10;

export const statsSchema = z.object({
  played: z.number().int().min(0),
  won: z.number().int().min(0),
  abandoned: z.number().int().min(0),
  winRate: z.number().min(0).max(1).nullable(),
  /** counts[i] is wins in i + 1 guesses; the last bucket is that many or more. */
  histogram: z.array(z.number().int().min(0)).length(HISTOGRAM_BUCKETS),
  averageGuesses: z.number().nullable(),
  bestGuesses: z.number().int().nullable(),
  fastestMs: z.number().int().nullable(),
  averageMs: z.number().nullable(),
  currentStreak: z.number().int().min(0),
  bestStreak: z.number().int().min(0),
  accuracy: z.object({
    guessesAnalysed: z.number().int().min(0),
    /** Share of guesses that could still have been the answer. */
    consistencyRate: z.number().min(0).max(1).nullable(),
    /** Mean of removed / most-removable over guesses. */
    eliminationEfficiency: z.number().min(0).max(1).nullable(),
    /** Won games only: your guesses against the solver's for the same answer. */
    pace: z
      .object({
        games: z.number().int().min(1),
        averageGuesses: z.number(),
        averagePar: z.number(),
        /** Positive means more guesses than the solver; negative means fewer. */
        averageDelta: z.number(),
      })
      .nullable(),
  }),
});
export type Stats = z.infer<typeof statsSchema>;
